'use client';

import { sellerTypeOptions, sortingOptions } from '@c/ProductsPage';
import { ArrowsUpDownIcon } from '@heroicons/react/20/solid';
import ButtonToggle from '@ui/ButtonToggle';
import Input from '@ui/Input';
import Select from '@ui/Select';
import { PageSideFilters, TopFilters } from '@util/hooks/useProductsPage';
import { ButtonToggleType } from '@util/types/buttontoggle';
import { motion } from 'framer-motion';
import { ChevronsUpDown, ListFilter } from 'lucide-react';
import { CategoryDocument } from 'models/category';
import { Condition, FacetCounts, ProductSortOption } from 'models/product';
import { SellerType } from 'models/shared';
import { MotionDiv } from 'motion';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import MobileSearchFilters from './MobileSearchFilters';
import MobileSearchSort from './MobileSearchSort';
import { cn } from '@util/index';

type SortingOption = {
  label: string;
  value: ProductSortOption;
  id: string;
  icon: JSX.Element;
};

type ToggleOption = {
  value: string;
  label: string;
};

const AnimatedToggle = ({
  options,
  value,
  onChange,
}: {
  options: ToggleOption[];
  value: string;
  onChange: (value: string) => void;
}) => {
  const BUTTON_WIDTH = 65; // Fixed width in pixels

  // Get the index of the active option
  const activeIndex = options.findIndex((option) => option.value === value);

  return (
    <div
      className="relative flex h-[3.8rem] items-center rounded-full border border-[#E5E5E5] bg-[#F7F7F7]"
      style={{ width: BUTTON_WIDTH * options.length }}
    >
      <motion.div
        className="absolute rounded-full bg-[#707070]"
        layoutId="activeToggle"
        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
        animate={{
          height: 'calc(100% - 0.5rem)',
          width: BUTTON_WIDTH - 8,
          left: `${activeIndex * BUTTON_WIDTH + 4}px`, // Position based on active index
        }}
      />
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`relative z-10 flex h-full w-[${BUTTON_WIDTH}px] items-center justify-center rounded-full text-[1.6rem] font-medium transition-colors
            ${
              value === option.value
                ? `text-white`
                : 'text-gray-700 hover:text-gray-900'
            }`}
          style={{ width: `${BUTTON_WIDTH}px` }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

type Props = {
  handleConditionsChange: (filters: Condition[]) => void;
  handleSellerTypeChange: (filters: SellerType[]) => void;
  handleSortChange: (option: SortingOption) => void;
  handleSearchChange: (value: string) => void;
  handleSaleChange: (value: boolean) => void;
  setDrawerOpen: (open: boolean) => void;
  setDropdownOpen: (open: boolean) => void;
  topFilters: TopFilters;
  showSearch: boolean | undefined;
  pageType: string;
  dropdownOpen: boolean;
  buttonToggles: ButtonToggleType[];
  filterState: PageSideFilters;
  setFilterState: (filterState: PageSideFilters) => void;
  currentCat: CategoryDocument | null;
  allBrands: string[];
  facet_counts: FacetCounts | undefined;
  categories?: CategoryDocument[];
  keyword?: string;
  mostly_parts?: boolean;
};

export default function SearchControls({
  handleConditionsChange,
  handleSellerTypeChange,
  handleSortChange,
  handleSearchChange,
  setDropdownOpen,
  topFilters,
  showSearch,
  pageType,
  dropdownOpen,
  buttonToggles,
  filterState,
  setFilterState,
  allBrands,
  facet_counts,
  categories,
  keyword,
  mostly_parts,
}: Props) {
  const [showFilters, setShowFilters] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const pathname = usePathname();

  const conditionOptions = [
    { value: 'all', label: 'All' },
    { value: 'new', label: 'New' },
    { value: 'used', label: 'Used' },
  ];

  const getCurrentCondition = () => {
    if (filterState.conditions.length === 0) return 'all';
    if (filterState.conditions.includes('New')) return 'new';
    return 'used';
  };

  const isSellerProfilePage = pathname?.includes('/profile');

  return (
    <div className="flex w-full flex-col gap-y-4 pl-2 pr-8">
      <div
        className={cn(
          'w-full gap-y-4 px-2',
          isSellerProfilePage && showSearch
            ? 'flex flex-col sm:hidden'
            : 'hidden'
        )}
      >
        {showSearch && (
          <div className="my-4 flex-1">
            <Input
              type="search"
              placeholder={
                pageType === 'profile'
                  ? 'Search this store...'
                  : 'Search anything...'
              }
              onChange={(e) => {
                handleSearchChange(e.target.value);
              }}
              initialValue={keyword}
              className="!h-[4.5rem] w-full !rounded-[2rem] border border-zinc-200"
              searchIconRight
            />
          </div>
        )}
        <div className="flex w-full items-center justify-between  sm:justify-between">
          <div className="ml-2 flex items-center gap-x-4">
            <ButtonToggle buttons={buttonToggles} />
          </div>
          <div className="flex items-center gap-x-4">
            <button
              type="button"
              className="flex items-center gap-x-2"
              onClick={() => setShowSort(true)}
            >
              <ChevronsUpDown className="h-10 w-10 text-black" />
              <span className="text-[1.8rem] font-medium text-black">Sort</span>
            </button>
            <button
              type="button"
              className="flex items-center gap-x-2"
              onClick={() => setShowFilters(true)}
            >
              <ListFilter className="h-10 w-10 text-black" />
              <span className="text-[1.8rem] font-medium text-black">
                Filter
              </span>
            </button>
          </div>
        </div>

        {/* Mobile search input */}

        <MobileSearchFilters
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          filterState={filterState}
          setFilterState={setFilterState}
          allBrands={allBrands}
          facet_counts={facet_counts}
          categories={categories}
          mostly_parts={mostly_parts}
          pageType={pageType}
        />
        <MobileSearchSort
          show={showSort}
          setShow={setShowSort}
          sortingBy={topFilters.sort}
          onSortingChange={handleSortChange}
          pageType={pageType}
          filterState={filterState}
        />
      </div>
      <div className="hidden w-full flex-row items-center justify-between gap-[1.6rem] sm:flex sm:items-start sm:px-0">
        <div className="flex items-center gap-x-4">
          <AnimatedToggle
            options={conditionOptions}
            value={getCurrentCondition()}
            onChange={(value) => {
              if (value === 'all') handleConditionsChange([]);
              else if (value === 'new') handleConditionsChange(['New']);
              else handleConditionsChange(['Used']);
            }}
          />

          {!isSellerProfilePage && (
            <Select
              placeholder="Seller Type"
              options={sellerTypeOptions.map((option) => ({
                label: option.label,
                value: option.value,
                id: option.key,
              }))}
              value={filterState.sellerType?.[0]?.toLowerCase() ?? ''}
              className="!h-[3.8rem] !w-[18rem]"
              onChange={(option) =>
                handleSellerTypeChange([option.value as SellerType])
              }
              rounded
            />
          )}
        </div>

        <div className="flex w-full flex-col items-end gap-4 sm:flex-row sm:items-center sm:justify-end">
          {showSearch && (
            <div className="hidden sm:block">
              <Input
                type="search"
                placeholder="Search anything..."
                onChange={(e) => {
                  handleSearchChange(e.target.value);
                }}
                initialValue={keyword}
                className="!h-[3.8rem] !rounded-[2rem] border border-zinc-200"
              />
            </div>
          )}
          <div className="hidden sm:block">
            <Select
              value={topFilters.sort}
              options={sortingOptions
                .filter((option) => {
                  if (pageType === 'profile') {
                    return option.id !== 'soldDate';
                  } else if (pageType === 'recent') {
                    return option.id !== 'bestMatch';
                  } else if (!filterState.recentlySold) {
                    return option.id !== 'soldDate';
                  }
                  return true;
                })
                .map((option) => ({
                  label: option.label,
                  value: option.id,
                  id: option.id,
                }))}
              onChange={(option) => {
                handleSortChange(option as SortingOption);
              }}
              allowEmpty={false}
              className="!h-[3.8rem] min-w-[18rem]"
              rounded
            />
          </div>

          <div className="flex items-center gap-x-2 ">
            <div className="sm:hidden">
              <button
                type="button"
                className="flex h-[3.5rem] w-[3.5rem] items-center justify-center rounded-full border border-zinc-200 p-2"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-label="Sort"
              >
                <ArrowsUpDownIcon width={44} height={44} />
              </button>

              {dropdownOpen && (
                <MotionDiv
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-[10rem] z-[90] mt-2 flex w-[20rem] flex-col gap-y-2 rounded-md bg-white shadow-lg sm:right-0"
                >
                  {sortingOptions
                    .filter((option) => {
                      if (pageType === 'profile') {
                        return option.id !== 'soldDate';
                      }
                      return true;
                    })
                    .map((option) => (
                      <button
                        key={option.id}
                        className="flex w-full items-center gap-x-2 px-4 py-2 text-left text-black hover:bg-gray-100"
                        onClick={() => {
                          handleSortChange(option);
                          setDropdownOpen(false);
                        }}
                        aria-label={option.label}
                      >
                        {option.icon} {option.label}
                      </button>
                    ))}
                </MotionDiv>
              )}
            </div>
            <div className="hidden sm:block">
              <ButtonToggle buttons={buttonToggles} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
